import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../App';
import { toast } from 'sonner';
import { 
  Upload, FileSpreadsheet, CheckCircle, AlertCircle, Info, History, Undo2, 
  ChevronDown, ChevronUp, Trash2, Search, RefreshCw, Link2, Edit2, Check, X, 
  TrendingUp, Building2, Briefcase, CheckCircle2, Save, Unlink, Download,
  Database, ChevronRight, Loader2, ExternalLink, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';

const formatCurrency = (amount) => {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
  if (typeof amount === 'number' && Math.abs(amount) >= 1000) return `₹${amount.toLocaleString('en-IN')}`;
  return `₹${amount?.toLocaleString('en-IN') || 0}`;
};

const formatValue = (value) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'number') {
    if (Math.abs(value) >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (Math.abs(value) >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    if (Math.abs(value) >= 1000) return value.toLocaleString('en-IN');
    return value.toString();
  }
  return String(value);
};

const Imports = ({ user, setUser }) => {
  const [searchParams] = useSearchParams();
  // Main tabs: 'upload', 'symbols', or 'data'
  const [mainTab, setMainTab] = useState(searchParams.get('tab') || 'upload');
  
  // ============ BULK UPLOAD STATE ============
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [uploadType, setUploadType] = useState('portfolio');
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [confirmReverseOpen, setConfirmReverseOpen] = useState(false);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [reversing, setReversing] = useState(false);

  // ============ SYMBOL MANAGEMENT STATE ============
  const [symbolLoading, setSymbolLoading] = useState(false);
  const [unmappedHoldings, setUnmappedHoldings] = useState([]);
  const [unlinkedMFs, setUnlinkedMFs] = useState([]);
  const [linkedMFs, setLinkedMFs] = useState([]);
  const [stocksWithRecommendations, setStocksWithRecommendations] = useState([]);
  const [symbolTab, setSymbolTab] = useState('health');
  const [mfSubTab, setMfSubTab] = useState('unlinked');
  
  // MF Linking state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedMF, setSelectedMF] = useState(null);
  const [linkingDialogOpen, setLinkingDialogOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  
  // Stock display name state
  const [editedNames, setEditedNames] = useState({});
  const [confirmedSymbols, setConfirmedSymbols] = useState(new Set());
  const [savingNames, setSavingNames] = useState(false);
  const [fetchingCompanyNames, setFetchingCompanyNames] = useState(false);

  // ============ SYMBOL HEALTH STATE (Unified) ============
  const [symbolHealth, setSymbolHealth] = useState({ issues: [], total_issues: 0 });
  const [symbolHealthLoading, setSymbolHealthLoading] = useState(false);
  const [healthTickerInputs, setHealthTickerInputs] = useState({});
  const [healthPriceInputs, setHealthPriceInputs] = useState({});
  const [healthMergerInputs, setHealthMergerInputs] = useState({}); // {displayName: {newCompany, newTicker, swapRatio, notes}}
  const [healthPrivateInputs, setHealthPrivateInputs] = useState({}); // {displayName: {price, notes}}
  const [duplicateTickerInputs, setDuplicateTickerInputs] = useState({}); // {displayName: newTicker}
  const [savingHealthItem, setSavingHealthItem] = useState(null);
  const [healthFilter, setHealthFilter] = useState('all'); // 'all', 'no_price', 'invalid_ticker', 'price_failed', 'private', 'unlisted'
  const [issueFilter, setIssueFilter] = useState('all'); // Filter by specific issue type
  const [healthSearch, setHealthSearch] = useState(''); // Search by company name
  const [expandedMergerRow, setExpandedMergerRow] = useState(null); // Track which row has merger form expanded
  const [expandedPrivateRow, setExpandedPrivateRow] = useState(null); // Track which row has private form expanded
  const [openActionDropdown, setOpenActionDropdown] = useState(null); // Track which dropdown is open
  
  // Merge duplicates state
  const [mergeModal, setMergeModal] = useState(null); // {ticker, symbols} when open
  const [mergeTargetName, setMergeTargetName] = useState('');
  const [mergeTicker, setMergeTicker] = useState('');
  const [merging, setMerging] = useState(false);
  
  // Duplicate holdings state
  const [duplicateHoldings, setDuplicateHoldings] = useState(null);
  const [duplicateHoldingsLoading, setDuplicateHoldingsLoading] = useState(false);
  const [mergeHoldingsModal, setMergeHoldingsModal] = useState(null); // duplicate group when open
  const [selectedHoldingToKeep, setSelectedHoldingToKeep] = useState(null);
  const [mergingHoldings, setMergingHoldings] = useState(false);
  
  // Legacy state (kept for MF linking)
  const [tickerMappingData, setTickerMappingData] = useState({ unmapped_stocks: [], auto_matched_count: 0 });
  const [symbolStats, setSymbolStats] = useState(null);
  
  // AI Smart Search state
  const [searchingStock, setSearchingStock] = useState(null);
  const [aiSearchResults, setAiSearchResults] = useState({});

  // ============ DATA INTEGRITY STATE ============
  const [datasets, setDatasets] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [dataSearchTerm, setDataSearchTerm] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // ============ DATA COMPARISON STATE ============
  const [comparisonFile, setComparisonFile] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [dataTab, setDataTab] = useState('compare'); // 'compare', 'integrity', or 'summary'
  const [memberSummary, setMemberSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // ============ CROSS-PAGE SYNC STATE ============
  const [unlistedCount, setUnlistedCount] = useState(0);
  const navigate = useNavigate();

  // ============ FETCH DATA ============
  useEffect(() => {
    fetchUploadHistory();
    if (mainTab === 'symbols') {
      fetchSymbolData();
    }
    if (mainTab === 'data') {
      fetchDatasets();
    }
  }, [mainTab]);

  // Fetch member summary when summary tab is selected
  useEffect(() => {
    if (mainTab === 'data' && dataTab === 'summary' && !memberSummary) {
      fetchMemberSummary();
    }
  }, [mainTab, dataTab]);

  const fetchMemberSummary = async () => {
    setLoadingSummary(true);
    try {
      const response = await api.get('/data-integrity/member-summary');
      setMemberSummary(response.data);
    } catch (error) {
      console.error('Error fetching member summary:', error);
      toast.error('Failed to load member summary');
    } finally {
      setLoadingSummary(false);
    }
  };

  const fetchUploadHistory = async () => {
    try {
      const res = await api.get('/upload-history');
      setUploadHistory(res.data);
    } catch (err) {
      console.error('Failed to load upload history');
    }
  };

  const fetchDatasets = async () => {
    setDataLoading(true);
    try {
      const response = await api.get('/data-integrity/datasets');
      setDatasets(response.data);
    } catch (error) {
      console.error('Error fetching datasets:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const fetchRecords = async (datasetId) => {
    setLoadingRecords(true);
    try {
      const response = await api.get(`/data-integrity/${datasetId}`);
      setRecords(response.data.records || []);
      setSelectedDataset(datasets.find(d => d.id === datasetId));
    } catch (error) {
      console.error('Error fetching records:', error);
      setRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  };

  const handleSaveRecord = async () => {
    if (!editingRecord || !selectedDataset) return;
    try {
      await api.put(`/data-integrity/${selectedDataset.id}/${editingRecord.id}`, editingRecord);
      setEditingRecord(null);
      fetchRecords(selectedDataset.id);
      toast.success('Record updated successfully');
    } catch (error) {
      toast.error('Error saving record');
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!selectedDataset) return;
    try {
      await api.delete(`/data-integrity/${selectedDataset.id}/${recordId}`);
      setShowDeleteConfirm(null);
      fetchRecords(selectedDataset.id);
      fetchDatasets();
      toast.success('Record deleted');
    } catch (error) {
      toast.error('Error deleting record');
    }
  };

  // ============ DATA COMPARISON FUNCTIONS ============
  const handleComparisonFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setComparisonFile(file);
      setComparisonResult(null);
    }
  };

  const runComparison = async () => {
    if (!comparisonFile) {
      toast.error('Please select an Excel file to compare');
      return;
    }

    setComparing(true);
    try {
      const formData = new FormData();
      formData.append('file', comparisonFile);

      const response = await api.post('/data-comparison/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setComparisonResult(response.data);
      toast.success(`Comparison complete: ${response.data.summary.discrepancies} discrepancies found`);
    } catch (error) {
      console.error('Comparison error:', error);
      toast.error(error.response?.data?.detail || 'Failed to run comparison');
    } finally {
      setComparing(false);
    }
  };

  const getIssueTypeColor = (issueType) => {
    switch (issueType) {
      case 'MISSING_IN_DB': return 'bg-red-100 text-red-800';
      case 'QUANTITY_MISMATCH': return 'bg-yellow-100 text-yellow-800';
      case 'PRICE_DIFFERENCE': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDisplayColumns = (datasetId) => {
    const columnMaps = {
      'holdings': ['symbol', 'quantity', 'purchase_price', 'pledged', 'member_id'],
      'family_members': ['name', 'relation', 'created_at'],
      'bank_accounts': ['bank_name', 'account_type', 'account_number', 'balance', 'od_limit'],
      'las_account_records': ['member_name', 'bank_name', 'pledged_value', 'eligible_loan', 'ltv_ratio'],
      'bajaj_loan_details': ['account_code', 'amount_used', 'available', 'sanction_limit', 'margin_percent', 'ltv_percent'],
      'bajaj_banker_data': ['account_code', 'banker_available', 'banker_used', 'used_percent'],
      'mtf_account_status': ['account_code', 'safety_cover_percent', 'securities_value', 'outstanding', 'limit'],
      'bajaj_current_status': ['account_code', 'limit', 'used', 'available'],
      'value_investments': ['category', 'market_value', 'amount_invested', 'gain', 'gain_percent'],
      'performance_snapshots': ['date', 'portfolio_value', 'total_investment', 'interest_paid'],
      'dividend_performance': ['date', 'portfolio_value', 'dividend_received', 'dividend_percent_of_investment'],
      'dividends': ['member_id', 'symbol', 'amount', 'date'],
      'transactions': ['symbol', 'transaction_type', 'quantity', 'price', 'date'],
    };
    return columnMaps[datasetId] || ['id'];
  };

  const filteredRecords = records.filter(record => {
    if (!dataSearchTerm) return true;
    const searchLower = dataSearchTerm.toLowerCase();
    return Object.values(record).some(val => 
      String(val).toLowerCase().includes(searchLower)
    );
  });

  const fetchSymbolData = useCallback(async () => {
    setSymbolLoading(true);
    setSymbolHealthLoading(true);
    setDuplicateHoldingsLoading(true);
    try {
      const [unmappedRes, unlinkedRes, linkedRes, statsRes, healthRes, dupHoldingsRes] = await Promise.all([
        api.get('/holdings/unmapped'),
        api.get('/mutual-funds/unlinked'),
        api.get('/mutual-funds/linked'),
        api.get('/symbols/stats'),
        api.get('/symbols/health').catch(() => ({ data: { issues: [], total_issues: 0 } })),
        api.get('/holdings/duplicates').catch(() => ({ data: { duplicate_groups: [], total_duplicate_groups: 0 } }))
      ]);
      setUnmappedHoldings(unmappedRes.data || []);
      setUnlinkedMFs(unlinkedRes.data || []);
      setLinkedMFs(linkedRes.data || []);
      setSymbolStats(statsRes.data || null);
      setSymbolHealth(healthRes.data || { issues: [], total_issues: 0 });
      setUnlistedCount(healthRes.data?.unlisted_count || 0);
      setDuplicateHoldings(dupHoldingsRes.data || { duplicate_groups: [], total_duplicate_groups: 0 });
      
      // Clear input states
      setHealthTickerInputs({});
      setHealthPriceInputs({});
    } catch (err) {
      toast.error('Failed to load symbol data');
    } finally {
      setSymbolLoading(false);
      setSymbolHealthLoading(false);
      setDuplicateHoldingsLoading(false);
    }
  }, []);

  // ============ BULK UPLOAD HANDLERS ============
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const results = [];
    let totalHoldings = 0;
    let totalLASAccounts = 0;
    let totalMTFAccounts = 0;
    let categories = [];
    let totalTransactions = 0;
    let totalSkippedDuplicates = 0;
    let totalSkippedZeroQty = 0;
    let cheatsheetStats = null;

    const endpoint = uploadType === 'mtf' 
      ? '/holdings/bulk-upload-mtf'
      : uploadType === 'cheatsheet'
      ? '/bulk-upload-cheatsheet'
      : '/holdings/bulk-upload-vallabhaneni';

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const res = await api.post(endpoint, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        results.push({
          filename: file.name,
          status: 'success',
          data: res.data,
          type: uploadType
        });

        if (uploadType === 'cheatsheet') {
          cheatsheetStats = res.data;
        } else {
          totalHoldings += res.data.holdings_count || res.data.holdings_added || 0;
          totalTransactions += res.data.transactions_count || 0;
          if (res.data.category) categories.push(res.data.category);
          totalLASAccounts += res.data.las_accounts_created || 0;
          totalMTFAccounts += res.data.mtf_accounts_created || 0;
          totalSkippedDuplicates += res.data.skipped_duplicates || 0;
          totalSkippedZeroQty += res.data.skipped_zero_qty || 0;
        }
      } catch (err) {
        results.push({
          filename: file.name,
          status: 'error',
          error: err.response?.data?.detail || 'Upload failed'
        });
      }
    }

    setUploadResults(results);
    setUploading(false);
    
    if (results.some(r => r.status === 'success')) {
      setUploadSummary(uploadType === 'cheatsheet' ? {
        type: 'cheatsheet',
        ...cheatsheetStats
      } : {
        type: uploadType,
        totalHoldings,
        totalTransactions,
        totalLASAccounts,
        totalMTFAccounts,
        totalSkippedDuplicates,
        totalSkippedZeroQty,
        categories
      });
      setSummaryOpen(true);
      fetchUploadHistory();
    }
    
    event.target.value = '';
  };

  const handleReverseUpload = async () => {
    if (!selectedUpload) return;
    setReversing(true);
    try {
      await api.post(`/upload-history/${selectedUpload.id}/reverse`);
      toast.success('Upload reversed successfully');
      fetchUploadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reverse upload');
    } finally {
      setReversing(false);
      setConfirmReverseOpen(false);
      setSelectedUpload(null);
    }
  };

  // ============ SYMBOL MANAGEMENT HANDLERS ============
  const searchMutualFunds = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await api.get(`/mf/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(res.data);
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const openLinkDialog = (mf) => {
    setSelectedMF(mf);
    setSearchQuery('');
    setSearchResults([]);
    setLinkingDialogOpen(true);
  };

  const linkMutualFund = async (schemeCode, schemeName) => {
    if (!selectedMF) return;
    setLinking(true);
    try {
      const res = await api.post(`/mutual-funds/link-bulk?symbol=${encodeURIComponent(selectedMF.symbol)}&scheme_code=${schemeCode}`);
      toast.success(res.data.message);
      setLinkingDialogOpen(false);
      setSelectedMF(null);
      fetchSymbolData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link mutual fund');
    } finally {
      setLinking(false);
    }
  };

  const unlinkMutualFund = async (symbol) => {
    if (!confirm(`Are you sure you want to unlink "${symbol.replace('O-', '')}"? You can re-link it later.`)) return;
    setUnlinking(true);
    try {
      const res = await api.post(`/mutual-funds/unlink?symbol=${encodeURIComponent(symbol)}`);
      toast.success(res.data.message);
      fetchSymbolData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlink mutual fund');
    } finally {
      setUnlinking(false);
    }
  };

  const handleNameChange = (symbol, name) => {
    setEditedNames(prev => ({ ...prev, [symbol]: name }));
    setConfirmedSymbols(prev => {
      const newSet = new Set(prev);
      newSet.delete(symbol);
      return newSet;
    });
  };

  const toggleConfirm = (symbol) => {
    setConfirmedSymbols(prev => {
      const newSet = new Set(prev);
      if (newSet.has(symbol)) {
        newSet.delete(symbol);
      } else {
        newSet.add(symbol);
      }
      return newSet;
    });
  };

  const saveConfirmedNames = async () => {
    if (confirmedSymbols.size === 0) return;
    setSavingNames(true);
    try {
      const updates = Array.from(confirmedSymbols).map(symbol => ({
        symbol,
        display_name: editedNames[symbol]
      }));
      await api.post('/stocks/update-display-names', { updates });
      toast.success(`Updated ${updates.length} stock names`);
      setConfirmedSymbols(new Set());
      fetchSymbolData();
    } catch (err) {
      toast.error('Failed to save names');
    } finally {
      setSavingNames(false);
    }
  };

  const fetchCompanyNames = async () => {
    setFetchingCompanyNames(true);
    try {
      const symbols = stocksWithRecommendations.map(s => s.symbol);
      const res = await api.post('/stocks/company-names', { symbols });
      const newNames = { ...editedNames };
      res.data.names.forEach(item => {
        if (item.company_name) {
          newNames[item.symbol] = item.company_name;
        }
      });
      setEditedNames(newNames);
      toast.success(`Fetched ${res.data.names.filter(n => n.company_name).length} company names`);
    } catch (err) {
      toast.error('Failed to fetch company names');
    } finally {
      setFetchingCompanyNames(false);
    }
  };

  // ============ TICKER MAPPING HANDLERS ============
  const handleTickerChange = (displayName, ticker) => {
    setEditedTickers(prev => ({ ...prev, [displayName]: ticker?.toUpperCase() || '' }));
    setSelectedForMapping(prev => {
      const newSet = new Set(prev);
      newSet.delete(displayName);
      return newSet;
    });
  };

  const toggleMappingSelection = (displayName) => {
    setSelectedForMapping(prev => {
      const newSet = new Set(prev);
      if (newSet.has(displayName)) {
        newSet.delete(displayName);
      } else {
        newSet.add(displayName);
      }
      return newSet;
    });
  };

  const selectAllMappings = () => {
    const allWithTickers = tickerMappingData.unmapped_stocks
      .filter(s => editedTickers[s.display_name])
      .map(s => s.display_name);
    setSelectedForMapping(new Set(allWithTickers));
    toast.success(`Selected ${allWithTickers.length} stocks with tickers`);
  };

  const saveSelectedMappings = async () => {
    if (selectedForMapping.size === 0) {
      toast.error('No stocks selected. Enter ticker symbols and click confirm.');
      return;
    }
    
    setSavingMappings(true);
    try {
      const mappings = Array.from(selectedForMapping)
        .filter(name => editedTickers[name])
        .map(name => ({
          display_name: name,
          ticker_symbol: editedTickers[name]
        }));
      
      const res = await api.post('/symbols/confirm-bulk', { mappings });
      toast.success(res.data.message);
      setSelectedForMapping(new Set());
      fetchSymbolData();
    } catch (err) {
      toast.error('Failed to save mappings');
    } finally {
      setSavingMappings(false);
    }
  };

  const autoMapHighConfidence = async () => {
    setAutoMapping(true);
    try {
      const res = await api.post('/symbols/auto-map-all');
      toast.success(res.data.message);
      fetchSymbolData();
    } catch (err) {
      toast.error('Failed to auto-map symbols');
    } finally {
      setAutoMapping(false);
    }
  };

  const [aiMatching, setAiMatching] = useState(false);
  
  const aiMatchUnmapped = async () => {
    setAiMatching(true);
    try {
      toast.info('AI is analyzing unmapped stocks... This may take 15-30 seconds.');
      const res = await api.post('/symbols/ai-match', {});
      if (res.data.matched_count > 0) {
        toast.success(`AI matched ${res.data.matched_count} symbols!`);
      } else {
        toast.info('AI could not find additional matches. Try manual entry.');
      }
      fetchSymbolData();
    } catch (err) {
      toast.error('AI matching failed. Try again later.');
    } finally {
      setAiMatching(false);
    }
  };

  const [selectedForUnlisted, setSelectedForUnlisted] = useState(new Set());
  const [markingUnlisted, setMarkingUnlisted] = useState(false);

  const toggleUnlistedSelection = (displayName) => {
    setSelectedForUnlisted(prev => {
      const newSet = new Set(prev);
      if (newSet.has(displayName)) {
        newSet.delete(displayName);
      } else {
        newSet.add(displayName);
      }
      return newSet;
    });
  };

  const markSelectedAsUnlisted = async () => {
    if (selectedForUnlisted.size === 0) {
      toast.error('No stocks selected to mark as unlisted');
      return;
    }
    
    setMarkingUnlisted(true);
    try {
      const res = await api.post('/symbols/mark-bulk-unlisted', {
        display_names: Array.from(selectedForUnlisted),
        reason: 'delisted'
      });
      toast.success(res.data.message);
      setSelectedForUnlisted(new Set());
      fetchSymbolData();
    } catch (err) {
      toast.error('Failed to mark as unlisted');
    } finally {
      setMarkingUnlisted(false);
    }
  };

  // ============ SYMBOL HEALTH HANDLERS ============
  const handleHealthTickerChange = (displayName, value) => {
    setHealthTickerInputs(prev => ({
      ...prev,
      [displayName]: value.toUpperCase()
    }));
  };

  const handleHealthPriceChange = (displayName, value) => {
    setHealthPriceInputs(prev => ({
      ...prev,
      [displayName]: value
    }));
  };

  const fixTickerMapping = async (displayName) => {
    const newTicker = healthTickerInputs[displayName];
    if (!newTicker) {
      toast.error('Please enter a valid ticker symbol');
      return;
    }
    
    setSavingHealthItem(displayName);
    try {
      await api.post('/symbols/confirm-mapping', {
        display_name: displayName,
        ticker_symbol: newTicker
      });
      toast.success(`Mapped ${displayName} → ${newTicker}. Refresh prices to update.`);
      setHealthTickerInputs(prev => {
        const updated = { ...prev };
        delete updated[displayName];
        return updated;
      });
      fetchSymbolData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save mapping');
    } finally {
      setSavingHealthItem(null);
    }
  };

  // Save notes for any symbol
  const saveNotes = async (displayName, notes) => {
    try {
      await api.post('/symbols/save-notes', {
        display_name: displayName,
        notes: notes
      });
      toast.success(`Notes saved for ${displayName}`);
      // Update local state to reflect the change
      setSymbolHealth(prev => ({
        ...prev,
        issues: prev.issues.map(issue => 
          issue.display_name === displayName 
            ? { ...issue, notes: notes }
            : issue
        )
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save notes');
    }
  };

  const markAsUnlisted = async (displayName) => {
    setSavingHealthItem(displayName);
    try {
      await api.post('/symbols/mark-bulk-unlisted', {
        display_names: [displayName],
        reason: 'delisted'
      });
      toast.success(`Marked ${displayName} as unlisted`);
      fetchSymbolData();
    } catch (err) {
      toast.error('Failed to mark as unlisted');
    } finally {
      setSavingHealthItem(null);
    }
  };

  const saveManualPrice = async (displayName) => {
    const priceValue = healthPriceInputs[displayName];
    if (!priceValue || isNaN(parseFloat(priceValue)) || parseFloat(priceValue) < 0) {
      toast.error('Please enter a valid price');
      return;
    }

    setSavingHealthItem(displayName);
    try {
      await api.post('/symbols/set-unlisted-price', {
        display_name: displayName,
        current_price: parseFloat(priceValue)
      });
      toast.success(`Price set to ₹${parseFloat(priceValue).toLocaleString()} for ${displayName}`);
      setHealthPriceInputs(prev => {
        const updated = { ...prev };
        delete updated[displayName];
        return updated;
      });
      fetchSymbolData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save price');
    } finally {
      setSavingHealthItem(null);
    }
  };

  const restoreToListed = async (displayName) => {
    const newTicker = healthTickerInputs[displayName];
    
    setSavingHealthItem(displayName);
    try {
      await api.post('/symbols/restore-to-listed', {
        display_name: displayName,
        ticker_symbol: newTicker || ''
      });
      toast.success(`Restored ${displayName} to listed stocks`);
      setHealthTickerInputs(prev => {
        const updated = { ...prev };
        delete updated[displayName];
        return updated;
      });
      fetchSymbolData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore stock');
    } finally {
      setSavingHealthItem(null);
    }
  };

  // Fix duplicate ticker mapping
  const handleFixDuplicateTicker = async (displayName, newTicker) => {
    if (!newTicker) {
      toast.error('Please enter a valid ticker symbol');
      return;
    }
    
    setSavingHealthItem(displayName);
    try {
      await api.post('/symbols/confirm-mapping', {
        display_name: displayName,
        ticker_symbol: newTicker
      });
      toast.success(`Mapped ${displayName} → ${newTicker}. Refresh prices to update.`);
      setDuplicateTickerInputs(prev => {
        const updated = { ...prev };
        delete updated[displayName];
        return updated;
      });
      fetchSymbolData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save mapping');
    } finally {
      setSavingHealthItem(null);
    }
  };

  // Mark stock as unlisted from duplicate tickers view
  const handleMarkUnlisted = async (displayName) => {
    setSavingHealthItem(displayName);
    try {
      await api.post('/symbols/mark-unlisted', {
        display_name: displayName,
        reason: 'no_yahoo_ticker'
      });
      toast.success(`Marked ${displayName} as unlisted`);
      setDuplicateTickerInputs(prev => {
        const updated = { ...prev };
        delete updated[displayName];
        return updated;
      });
      fetchSymbolData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as unlisted');
    } finally {
      setSavingHealthItem(null);
    }
  };

  // Open merge modal for a duplicate group
  const openMergeModal = (dup) => {
    setMergeModal(dup);
    // Default to the first symbol name and current ticker
    setMergeTargetName(dup.symbols[0]?.display_name || '');
    setMergeTicker(dup.ticker);
  };

  // Handle merge duplicates
  const handleMergeDuplicates = async () => {
    if (!mergeModal || !mergeTargetName || !mergeTicker) {
      toast.error('Please enter a target name and ticker');
      return;
    }

    setMerging(true);
    try {
      const sourceSymbols = mergeModal.symbols.map(s => s.display_name);
      const response = await api.post('/symbols/merge-duplicates', {
        source_symbols: sourceSymbols,
        target_symbol: mergeTargetName,
        ticker_symbol: mergeTicker
      });
      
      toast.success(response.data.message);
      setMergeModal(null);
      setMergeTargetName('');
      setMergeTicker('');
      fetchSymbolData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to merge symbols');
    } finally {
      setMerging(false);
    }
  };

  // Open merge holdings modal
  const openMergeHoldingsModal = (group) => {
    setMergeHoldingsModal(group);
    setSelectedHoldingToKeep(group.holdings[0]?.id || null);
  };

  // Handle merge duplicate holdings
  const handleMergeHoldings = async () => {
    if (!mergeHoldingsModal || !selectedHoldingToKeep) {
      toast.error('Please select a holding to keep');
      return;
    }

    setMergingHoldings(true);
    try {
      const holdingIds = mergeHoldingsModal.holdings.map(h => h.id);
      const response = await api.post('/holdings/merge-duplicates', {
        holding_ids: holdingIds,
        keep_holding_id: selectedHoldingToKeep,
        merge_quantities: true
      });
      
      toast.success(response.data.message);
      setMergeHoldingsModal(null);
      setSelectedHoldingToKeep(null);
      fetchSymbolData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to merge holdings');
    } finally {
      setMergingHoldings(false);
    }
  };

  // Delete a single holding
  const handleDeleteHolding = async (holdingId, symbol) => {
    if (!window.confirm(`Are you sure you want to delete this holding of ${symbol}? This cannot be undone.`)) {
      return;
    }

    setSavingHealthItem(holdingId);
    try {
      await api.delete(`/holdings/${holdingId}`);
      toast.success('Holding deleted successfully');
      fetchSymbolData();
      // Close modal if open and this was the last holding
      if (mergeHoldingsModal) {
        const remaining = mergeHoldingsModal.holdings.filter(h => h.id !== holdingId);
        if (remaining.length <= 1) {
          setMergeHoldingsModal(null);
        } else {
          setMergeHoldingsModal({
            ...mergeHoldingsModal,
            holdings: remaining
          });
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete holding');
    } finally {
      setSavingHealthItem(null);
    }
  };

  // Clear a stock from the review list
  const clearReview = async (displayName) => {
    setSavingHealthItem(displayName);
    try {
      await api.post('/symbols/clear-review', {
        display_name: displayName
      });
      toast.success(`Cleared ${displayName} from review list`);
      fetchSymbolData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear from review');
    } finally {
      setSavingHealthItem(null);
    }
  };

  // Handle merger info input changes
  const handleMergerInputChange = (displayName, field, value) => {
    setHealthMergerInputs(prev => ({
      ...prev,
      [displayName]: {
        ...(prev[displayName] || {}),
        [field]: value
      }
    }));
  };

  // Handle private info input changes
  const handlePrivateInputChange = (displayName, field, value) => {
    setHealthPrivateInputs(prev => ({
      ...prev,
      [displayName]: {
        ...(prev[displayName] || {}),
        [field]: value
      }
    }));
  };

  // Mark stock as merged with another company
  const markAsMerged = async (displayName) => {
    const mergerData = healthMergerInputs[displayName] || {};
    if (!mergerData.newCompany) {
      toast.error('Please enter the new company name');
      return;
    }

    setSavingHealthItem(displayName);
    try {
      await api.post('/symbols/mark-merged', {
        display_name: displayName,
        new_company_name: mergerData.newCompany,
        new_ticker: mergerData.newTicker || null,
        swap_ratio: mergerData.swapRatio || null,
        merger_notes: mergerData.notes || null
      });
      
      toast.success(`${displayName} marked as merged with ${mergerData.newCompany}`);
      setHealthMergerInputs(prev => {
        const updated = { ...prev };
        delete updated[displayName];
        return updated;
      });
      setExpandedMergerRow(null);
      setOpenActionDropdown(null);
      fetchSymbolData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as merged');
    } finally {
      setSavingHealthItem(null);
    }
  };

  // Mark stock as private with optional manual price
  const markAsPrivate = async (displayName) => {
    const privateData = healthPrivateInputs[displayName] || {};
    
    setSavingHealthItem(displayName);
    try {
      await api.post('/symbols/mark-private', {
        display_name: displayName,
        manual_price: privateData.price ? parseFloat(privateData.price) : null,
        notes: privateData.notes || null
      });
      
      toast.success(`${displayName} marked as private${privateData.price ? ` with price ₹${privateData.price}` : ''}`);
      setHealthPrivateInputs(prev => {
        const updated = { ...prev };
        delete updated[displayName];
        return updated;
      });
      setExpandedPrivateRow(null);
      setOpenActionDropdown(null);
      fetchSymbolData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as private');
    } finally {
      setSavingHealthItem(null);
    }
  };

  // Save merger/swap info for a stock (legacy - for unlisted stocks)
  const saveMergerInfo = async (displayName) => {
    const mergerData = healthMergerInputs[displayName] || {};
    if (!mergerData.newCompany) {
      toast.error('Please enter the new company name');
      return;
    }

    setSavingHealthItem(displayName);
    try {
      await api.post('/symbols/unlisted/update-merger-info', {
        display_name: displayName,
        new_company_name: mergerData.newCompany,
        new_ticker: mergerData.newTicker || null,
        swap_ratio: mergerData.swapRatio || null,
        is_private: mergerData.isPrivate || false
      });
      
      toast.success(`Merger info saved for ${displayName}`);
      setHealthMergerInputs(prev => {
        const updated = { ...prev };
        delete updated[displayName];
        return updated;
      });
      setExpandedMergerRow(null);
      fetchSymbolData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save merger info');
    } finally {
      setSavingHealthItem(null);
    }
  };

  // Move a stock to Symbol Health for investigation (from Compare Excel vs DB)
  const moveToSymbolHealth = async (displayName, reason = 'data_discrepancy') => {
    try {
      // Mark as needing review by setting a flag in symbol_mappings
      await api.post('/symbols/mark-for-review', {
        display_name: displayName,
        reason: reason
      });
      toast.success(`${displayName} marked for review in Symbol Health`);
      fetchSymbolData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark for review');
    }
  };

  // Get unique issue types from symbol health data
  const uniqueIssueTypes = [...new Set(symbolHealth.issues?.map(i => i.reason) || [])].sort();

  // Filter symbol health issues
  const filteredHealthIssues = (symbolHealth.issues || []).filter(issue => {
    // Filter by status (card clicks)
    if (healthFilter !== 'all' && issue.status !== healthFilter) {
      return false;
    }
    // Filter by specific issue type
    if (issueFilter !== 'all' && issue.reason !== issueFilter) {
      return false;
    }
    // Filter by search text
    if (healthSearch && !issue.display_name.toLowerCase().includes(healthSearch.toLowerCase())) {
      return false;
    }
    return true;
  });


  // AI Smart Search for a single stock
  const smartSearchStock = async (displayName) => {
    setSearchingStock(displayName);
    try {
      const res = await api.post('/symbols/smart-search', { display_name: displayName });
      setAiSearchResults(prev => ({ ...prev, [displayName]: res.data }));
      
      const data = res.data;
      
      // If currently trading, auto-fill the ticker
      if (data.currently_trading) {
        const ticker = data.nse_symbol || data.bse_symbol;
        if (ticker) {
          setEditedTickers(prev => ({ ...prev, [displayName]: ticker }));
          const exchange = data.nse_symbol ? 'NSE' : 'BSE';
          toast.success(
            <div>
              <strong>✓ Found: {ticker}</strong>
              <div className="text-sm">{data.company_name || displayName}</div>
              <div className="text-xs text-gray-500">Trading on {exchange}</div>
            </div>,
            { duration: 5000 }
          );
        }
      } else {
        // Not trading - show detailed status
        const statusText = {
          'delisted': '❌ Delisted',
          'merged': '🔀 Merged with another company',
          'suspended': '⏸️ Suspended',
          'name_changed': '📝 Name Changed',
          'unknown': '❓ Status Unknown'
        }[data.status] || data.status;
        
        toast.info(
          <div>
            <strong>{statusText}</strong>
            {data.company_name && <div className="text-sm">{data.company_name}</div>}
            {data.details && <div className="text-xs text-gray-500">{data.details}</div>}
          </div>,
          { duration: 6000 }
        );
      }
    } catch (err) {
      toast.error('AI search failed. Please try again.');
    } finally {
      setSearchingStock(null);
    }
  };

  // Sort and filter unmapped stocks
  const getSortedFilteredStocks = () => {
    let stocks = [...(tickerMappingData.unmapped_stocks || [])];
    
    // Apply filter
    if (tickerFilter === 'with_suggestion') {
      stocks = stocks.filter(s => s.suggested_ticker);
    } else if (tickerFilter === 'no_suggestion') {
      stocks = stocks.filter(s => !s.suggested_ticker);
    }
    
    // Apply sort
    stocks.sort((a, b) => {
      let aVal, bVal;
      switch (tickerSortKey) {
        case 'name':
          aVal = a.display_name.toLowerCase();
          bVal = b.display_name.toLowerCase();
          break;
        case 'suggested':
          aVal = a.suggested_ticker || 'zzz';
          bVal = b.suggested_ticker || 'zzz';
          break;
        case 'value':
        default:
          aVal = a.total_value || 0;
          bVal = b.total_value || 0;
          break;
      }
      
      if (tickerSortDir === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
    
    return stocks;
  };

  const toggleTickerSort = (key) => {
    if (tickerSortKey === key) {
      setTickerSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setTickerSortKey(key);
      setTickerSortDir('desc');
    }
  };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Imports</h1>
            <p className="text-gray-500 text-sm">Upload portfolio data and manage symbol mappings</p>
          </div>
        </div>

        {/* Main Tabs */}
        <div className="flex gap-1 border-b overflow-x-auto">
          <button
            onClick={() => setMainTab('upload')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
              mainTab === 'upload' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Upload size={16} className="inline mr-2" />
            Bulk Upload
          </button>
          <button
            onClick={() => setMainTab('symbols')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
              mainTab === 'symbols' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Link2 size={16} className="inline mr-2" />
            Symbols
          </button>
          <button
            onClick={() => setMainTab('data')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
              mainTab === 'data' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Database size={16} className="inline mr-2" />
            Data Integrity
          </button>
        </div>

        {/* ============ BULK UPLOAD TAB ============ */}
        {mainTab === 'upload' && (
          <div className="space-y-6">
            {/* New Reconciliation Feature Banner */}
            <div className="card p-4 border-l-4 border-emerald-500 bg-emerald-50/50" data-testid="snapshot-reconciliation-banner">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <FileSpreadsheet className="text-emerald-700" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-900">Holdings Snapshot Reconciliation</p>
                    <p className="text-xs text-emerald-700 mt-0.5">
                      Got an Excel file with your authoritative holdings? Upload it to see a side-by-side diff and optionally sync.
                      Detects qty mismatches, price drift, missing holdings.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/imports/holdings-snapshot')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap"
                  data-testid="open-snapshot-reconciler-btn"
                >
                  Open Reconciler →
                </button>
              </div>
            </div>

            {/* Upload Type Selection */}
            <div className="card">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <FileSpreadsheet size={20} />
                Select Upload Type
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { id: 'portfolio', label: 'Portfolio Holdings', desc: 'Demat/LAS holdings with transactions' },
                  { id: 'mtf', label: 'MTF Holdings', desc: 'Margin trading facility data' },
                  { id: 'cheatsheet', label: 'Cheatsheet', desc: 'Stock categorization data' }
                ].map(type => (
                  <label
                    key={type.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      uploadType === type.id ? 'border-primary bg-[#D1FAE5]' : 'hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="uploadType"
                      value={type.id}
                      checked={uploadType === type.id}
                      onChange={(e) => setUploadType(e.target.value)}
                      className="sr-only"
                    />
                    <p className="font-medium">{type.label}</p>
                    <p className="text-sm text-gray-500">{type.desc}</p>
                  </label>
                ))}
              </div>
            </div>

            {/* Upload Area */}
            <div className="card">
              <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className={`w-12 h-12 mb-3 ${uploading ? 'animate-bounce text-primary' : 'text-gray-400'}`} />
                  <p className="mb-2 text-sm text-gray-500">
                    {uploading ? 'Uploading...' : <><span className="font-semibold">Click to upload</span> or drag and drop</>}
                  </p>
                  <p className="text-xs text-gray-500">Excel files (.xlsx, .xls)</p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".xlsx,.xls"
                  multiple
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
              </label>
            </div>

            {/* Upload History */}
            <div className="card">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="w-full flex items-center justify-between"
              >
                <h3 className="font-semibold flex items-center gap-2">
                  <History size={20} />
                  Upload History
                </h3>
                {showHistory ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>

              {showHistory && (
                <div className="mt-4 space-y-3">
                  {uploadHistory.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No upload history</p>
                  ) : (
                    uploadHistory.map((upload) => (
                      <div key={upload.id} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{upload.filename}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(upload.uploaded_at).toLocaleString()} · {upload.upload_type}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded ${
                              upload.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {upload.status}
                            </span>
                            {upload.status === 'active' && !upload.is_cheatsheet && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600"
                                onClick={() => {
                                  setSelectedUpload(upload);
                                  setConfirmReverseOpen(true);
                                }}
                              >
                                <Undo2 size={14} />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============ SYMBOL MANAGEMENT TAB ============ */}
        {mainTab === 'symbols' && (
          <div className="space-y-4">
            {symbolLoading ? (
              <div className="card text-center py-8">
                <RefreshCw size={32} className="mx-auto mb-2 animate-spin text-primary" />
                <p>Loading symbol data...</p>
              </div>
            ) : (
              <>
                {/* Symbol Health Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
                  <div className="bg-green-50 rounded-lg border border-green-200 p-4">
                    <p className="text-xs text-green-600 uppercase">✓ Healthy</p>
                    <p className="text-2xl font-bold text-green-700">{symbolHealth.healthy_count || 0}</p>
                    <p className="text-xs text-green-500 mt-1">Valid prices</p>
                  </div>
                  <div 
                    className={`rounded-lg border p-4 cursor-pointer transition-colors ${
                      healthFilter === 'no_price' 
                        ? 'bg-red-100 border-red-400' 
                        : 'bg-red-50 border-red-200 hover:bg-red-100'
                    }`}
                    onClick={() => setHealthFilter(healthFilter === 'no_price' ? 'all' : 'no_price')}
                  >
                    <p className="text-xs text-red-600 uppercase">🔴 No Price</p>
                    <p className="text-2xl font-bold text-red-700">{symbolHealth.no_price_count || 0}</p>
                    <p className="text-xs text-red-500 mt-1">Need mapping</p>
                  </div>
                  <div 
                    className={`rounded-lg border p-4 cursor-pointer transition-colors ${
                      healthFilter === 'invalid_ticker' 
                        ? 'bg-orange-100 border-orange-400' 
                        : 'bg-orange-50 border-orange-200 hover:bg-orange-100'
                    }`}
                    onClick={() => setHealthFilter(healthFilter === 'invalid_ticker' ? 'all' : 'invalid_ticker')}
                  >
                    <p className="text-xs text-orange-600 uppercase">⚠️ Invalid Ticker</p>
                    <p className="text-2xl font-bold text-orange-700">{symbolHealth.invalid_ticker_count || 0}</p>
                    <p className="text-xs text-orange-500 mt-1">Wrong mapping</p>
                  </div>
                  <div 
                    className={`rounded-lg border p-4 cursor-pointer transition-colors ${
                      healthFilter === 'price_failed' 
                        ? 'bg-yellow-100 border-yellow-400' 
                        : 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100'
                    }`}
                    onClick={() => setHealthFilter(healthFilter === 'price_failed' ? 'all' : 'price_failed')}
                  >
                    <p className="text-xs text-yellow-600 uppercase">🟠 Stale Price</p>
                    <p className="text-2xl font-bold text-yellow-700">{symbolHealth.price_failed_count || 0}</p>
                    <p className="text-xs text-yellow-500 mt-1">Refresh failed</p>
                  </div>
                  <div 
                    className={`rounded-lg border p-4 cursor-pointer transition-colors ${
                      healthFilter === 'private' 
                        ? 'bg-emerald-100 border-emerald-400' 
                        : 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                    }`}
                    onClick={() => setHealthFilter(healthFilter === 'private' ? 'all' : 'private')}
                  >
                    <p className="text-xs text-emerald-600 uppercase">🟢 Private</p>
                    <p className="text-2xl font-bold text-emerald-700">{symbolHealth.private_count || 0}</p>
                    <p className="text-xs text-emerald-500 mt-1">Manual price set</p>
                  </div>
                  <div 
                    className={`rounded-lg border p-4 cursor-pointer transition-colors ${
                      healthFilter === 'unlisted' 
                        ? 'bg-amber-100 border-amber-400' 
                        : 'bg-amber-50 border-amber-200 hover:bg-amber-100'
                    }`}
                    onClick={() => setHealthFilter(healthFilter === 'unlisted' ? 'all' : 'unlisted')}
                  >
                    <p className="text-xs text-amber-600 uppercase">🟡 Unlisted</p>
                    <p className="text-2xl font-bold text-amber-700">{symbolHealth.unlisted_count || 0}</p>
                    <p className="text-xs text-amber-500 mt-1">Need pricing</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                    <p className="text-xs text-blue-600 uppercase">Mutual Funds</p>
                    <p className="text-2xl font-bold text-blue-700">{unlinkedMFs.length + linkedMFs.length}</p>
                    <p className="text-xs text-blue-500 mt-1">{linkedMFs.length} linked</p>
                  </div>
                  <div 
                    className={`rounded-lg border p-4 cursor-pointer transition-colors ${
                      healthFilter === 'duplicate' 
                        ? 'bg-purple-100 border-purple-400' 
                        : 'bg-purple-50 border-purple-200 hover:bg-purple-100'
                    }`}
                    onClick={() => setHealthFilter(healthFilter === 'duplicate' ? 'all' : 'duplicate')}
                  >
                    <p className="text-xs text-purple-600 uppercase">🔀 Ticker Dups</p>
                    <p className="text-2xl font-bold text-purple-700">{symbolHealth.duplicate_ticker_groups || 0}</p>
                    <p className="text-xs text-purple-500 mt-1">{symbolHealth.duplicate_symbols_count || 0} extra symbols</p>
                  </div>
                  <div 
                    className={`rounded-lg border p-4 cursor-pointer transition-colors ${
                      symbolTab === 'holding-duplicates'
                        ? 'bg-red-100 border-red-400' 
                        : 'bg-red-50 border-red-200 hover:bg-red-100'
                    }`}
                    onClick={() => setSymbolTab('holding-duplicates')}
                  >
                    <p className="text-xs text-red-600 uppercase">⚠️ Holding Dups</p>
                    <p className="text-2xl font-bold text-red-700">{duplicateHoldings?.total_duplicate_groups || 0}</p>
                    <p className="text-xs text-red-500 mt-1">{duplicateHoldings?.total_extra_holdings || 0} extra records</p>
                  </div>
                </div>

                {/* Symbol Sub-tabs */}
                <div className="flex gap-1 border-b overflow-x-auto">
                  <button
                    onClick={() => setSymbolTab('health')}
                    className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
                      symbolTab === 'health' 
                        ? 'border-primary text-primary' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    🏥 Symbol Health ({symbolHealth.total_issues || 0})
                  </button>
                  <button
                    onClick={() => setSymbolTab('duplicates')}
                    className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
                      symbolTab === 'duplicates' 
                        ? 'border-purple-500 text-purple-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    🔀 Duplicate Tickers ({symbolHealth.duplicate_ticker_groups || 0})
                  </button>
                  <button
                    onClick={() => setSymbolTab('holding-duplicates')}
                    className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
                      symbolTab === 'holding-duplicates' 
                        ? 'border-red-500 text-red-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    ⚠️ Duplicate Holdings ({duplicateHoldings?.total_duplicate_groups || 0})
                  </button>
                  <button
                    onClick={() => setSymbolTab('mutual-funds')}
                    className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
                      symbolTab === 'mutual-funds' 
                        ? 'border-primary text-primary' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    📊 Mutual Funds ({unlinkedMFs.length + linkedMFs.length})
                  </button>
                </div>

                {/* ============ DUPLICATE TICKERS SECTION ============ */}
                {symbolTab === 'duplicates' && (
                  <div className="space-y-4">
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <h3 className="font-semibold text-purple-800 mb-2">Why does this matter?</h3>
                      <p className="text-sm text-purple-700">
                        When multiple display names share the same ticker, only <strong>{symbolHealth.unique_tickers || 0}</strong> unique prices are fetched 
                        (instead of {symbolHealth.total_listed_symbols || 0} symbols). This causes incorrect valuations when stocks like "ITC" and "ITC Hotels" 
                        use the same ticker but should have different prices.
                      </p>
                    </div>
                    
                    {(symbolHealth.duplicate_tickers || []).length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <span className="text-4xl">✅</span>
                        <p className="mt-2">No duplicate ticker mappings found!</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {(symbolHealth.duplicate_tickers || []).map((dup, idx) => (
                          <div key={idx} className="border border-purple-200 rounded-lg p-4 bg-white">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <span className="font-mono font-bold text-purple-700 text-lg">{dup.ticker}</span>
                                <span className="ml-2 text-sm text-gray-500">({dup.count} symbols using this ticker)</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => openMergeModal(dup)}
                                  className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                                >
                                  🔗 Merge All
                                </button>
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                                  Fix needed
                                </span>
                              </div>
                            </div>
                            <div className="space-y-2">
                              {dup.symbols.map((sym, sIdx) => (
                                <div key={sIdx} className="bg-gray-50 rounded px-3 py-2">
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <span className="font-medium">{sym.display_name}</span>
                                      <span className="text-xs text-gray-500 ml-2">
                                        ({sym.quantity?.toLocaleString('en-IN')} qty)
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {sym.current_price ? (
                                        <span className="text-green-600 text-sm">₹{sym.current_price.toLocaleString('en-IN')}</span>
                                      ) : (
                                        <span className="text-red-500 text-sm">No price</span>
                                      )}
                                      <input
                                        type="text"
                                        placeholder="New ticker"
                                        value={duplicateTickerInputs[sym.display_name] || ''}
                                        onChange={(e) => setDuplicateTickerInputs(prev => ({
                                          ...prev,
                                          [sym.display_name]: e.target.value.toUpperCase()
                                        }))}
                                        className="w-24 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-purple-400 uppercase"
                                      />
                                      <button
                                        onClick={() => handleFixDuplicateTicker(sym.display_name, duplicateTickerInputs[sym.display_name])}
                                        disabled={!duplicateTickerInputs[sym.display_name]}
                                        className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                      >
                                        Fix
                                      </button>
                                      <button
                                        onClick={() => handleMarkUnlisted(sym.display_name)}
                                        className="px-2 py-1 text-xs bg-amber-500 text-white rounded hover:bg-amber-600"
                                        title="Mark as unlisted"
                                      >
                                        Unlisted
                                      </button>
                                    </div>
                                  </div>
                                  {/* Holder names */}
                                  <div className="mt-1 text-xs text-gray-500">
                                    <span className="font-medium">Holders:</span>{' '}
                                    {sym.holders?.join(', ') || 'Unknown'}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <p className="text-xs text-orange-600 mt-2 italic">
                              ⚠️ {dup.issue}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ============ MERGE MODAL ============ */}
                {mergeModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-gray-900">
                            🔗 Merge Duplicate Symbols
                          </h3>
                          <button
                            onClick={() => setMergeModal(null)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            ✕
                          </button>
                        </div>
                        
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                          <p className="text-sm text-blue-800">
                            This will rename all holdings from the symbols below to the target name. 
                            This is useful when the same company appears with different names in your portfolio.
                          </p>
                        </div>

                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Symbols to Merge ({mergeModal.symbols?.length})
                          </label>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {mergeModal.symbols?.map((sym, idx) => (
                              <div key={idx} className="bg-gray-50 rounded px-3 py-2 text-sm">
                                <div className="font-medium">{sym.display_name}</div>
                                <div className="text-xs text-gray-500">
                                  {sym.quantity?.toLocaleString('en-IN')} qty • Holders: {sym.holders?.join(', ') || 'Unknown'}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Merged Name (Target)
                          </label>
                          <input
                            type="text"
                            value={mergeTargetName}
                            onChange={(e) => setMergeTargetName(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter the final name for this stock"
                          />
                          <div className="mt-2 flex flex-wrap gap-1">
                            <span className="text-xs text-gray-500">Quick select:</span>
                            {mergeModal.symbols?.map((sym, idx) => (
                              <button
                                key={idx}
                                onClick={() => setMergeTargetName(sym.display_name)}
                                className={`text-xs px-2 py-0.5 rounded ${
                                  mergeTargetName === sym.display_name
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                              >
                                {sym.display_name}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="mb-6">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Ticker Symbol
                          </label>
                          <input
                            type="text"
                            value={mergeTicker}
                            onChange={(e) => setMergeTicker(e.target.value.toUpperCase())}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
                            placeholder="e.g., JIOFIN"
                          />
                        </div>

                        <div className="flex justify-end gap-3">
                          <button
                            onClick={() => setMergeModal(null)}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleMergeDuplicates}
                            disabled={merging || !mergeTargetName || !mergeTicker}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            {merging ? 'Merging...' : `Merge ${mergeModal.symbols?.length} Symbols`}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ============ DUPLICATE HOLDINGS SECTION ============ */}
                {symbolTab === 'holding-duplicates' && (
                  <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h3 className="font-semibold text-red-800 mb-2">What are Duplicate Holdings?</h3>
                      <p className="text-sm text-red-700">
                        These are <strong>TRUE duplicates</strong> - identical records with the same quantity AND price 
                        (likely from duplicate imports). Different purchases at different prices are NOT shown here.
                      </p>
                    </div>
                    
                    {duplicateHoldingsLoading ? (
                      <div className="text-center py-8 text-gray-500">Loading...</div>
                    ) : (duplicateHoldings?.duplicate_groups || []).length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <span className="text-4xl">✅</span>
                        <p className="mt-2">No duplicate holdings found!</p>
                        <p className="text-xs text-gray-400 mt-1">Multiple purchases of the same stock at different prices are legitimate, not duplicates.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {(duplicateHoldings?.duplicate_groups || []).map((group, idx) => (
                          <div key={idx} className="border border-red-200 rounded-lg p-4 bg-white">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <span className="font-bold text-red-700">{group.symbol}</span>
                                <span className="ml-2 text-sm text-gray-600">
                                  • {group.member_name} • {group.holdings_count} identical records
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                                  {group.reason || `${group.holdings_count}x identical`}
                                </span>
                                <button
                                  onClick={() => openMergeHoldingsModal(group)}
                                  className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 font-medium"
                                >
                                  🔗 Merge
                                </button>
                              </div>
                            </div>
                            <div className="space-y-1">
                              {group.holdings.map((h, hIdx) => (
                                <div key={hIdx} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 text-sm">
                                  <div className="flex items-center gap-4">
                                    <span className="font-mono text-xs text-gray-400">{h.id?.slice(0, 8)}...</span>
                                    <span className="font-medium">{h.quantity?.toLocaleString('en-IN')} qty</span>
                                    <span className="text-gray-500">@ ₹{h.purchase_price?.toLocaleString('en-IN')}</span>
                                    {h.quality_tag && (
                                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        h.quality_tag === 'High' ? 'bg-green-100 text-green-800' :
                                        h.quality_tag === 'Moderate' ? 'bg-blue-100 text-blue-800' :
                                        h.quality_tag === 'Low' ? 'bg-yellow-100 text-yellow-800' :
                                        h.quality_tag === 'V Low' ? 'bg-orange-100 text-orange-800' :
                                        h.quality_tag === 'VV Low' ? 'bg-red-100 text-red-800' :
                                        h.quality_tag === 'New' ? 'bg-purple-100 text-purple-800' :
                                        'bg-gray-100 text-gray-800'
                                      }`}>
                                        {h.quality_tag}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 text-xs">
                                    {h.pledged && <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Pledged</span>}
                                    {h.status !== 'free' && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{h.status}</span>}
                                    <span className="text-gray-400">{h.purchase_date?.slice(0, 10)}</span>
                                    <button
                                      onClick={() => handleDeleteHolding(h.id, group.symbol)}
                                      disabled={savingHealthItem === h.id}
                                      className="ml-2 px-2 py-0.5 text-red-600 hover:bg-red-100 rounded disabled:opacity-50"
                                      title="Delete this holding"
                                    >
                                      {savingHealthItem === h.id ? '...' : '🗑️'}
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ============ MERGE HOLDINGS MODAL ============ */}
                {mergeHoldingsModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-gray-900">
                            🔗 Merge Duplicate Holdings
                          </h3>
                          <button
                            onClick={() => setMergeHoldingsModal(null)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            ✕
                          </button>
                        </div>
                        
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                          <p className="text-sm text-red-800">
                            <strong>{mergeHoldingsModal.symbol}</strong> for <strong>{mergeHoldingsModal.member_name}</strong>
                            <br />
                            This will combine all quantities into one holding and delete the duplicates.
                          </p>
                        </div>

                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select which holding to keep (others will be merged into it):
                          </label>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {mergeHoldingsModal.holdings?.map((h, idx) => (
                              <div 
                                key={idx} 
                                className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
                                  selectedHoldingToKeep === h.id 
                                    ? 'border-red-500 bg-red-50' 
                                    : 'border-gray-200 hover:bg-gray-50'
                                }`}
                              >
                                <label className="flex items-center gap-3 flex-1 cursor-pointer">
                                  <input
                                    type="radio"
                                    name="keepHolding"
                                    value={h.id}
                                    checked={selectedHoldingToKeep === h.id}
                                    onChange={() => setSelectedHoldingToKeep(h.id)}
                                    className="text-red-600"
                                  />
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium">{h.quantity?.toLocaleString('en-IN')} qty @ ₹{h.purchase_price?.toLocaleString('en-IN')}</span>
                                      {h.quality_tag && (
                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                          h.quality_tag === 'High' ? 'bg-green-100 text-green-800' :
                                          h.quality_tag === 'Moderate' ? 'bg-blue-100 text-blue-800' :
                                          h.quality_tag === 'Low' ? 'bg-yellow-100 text-yellow-800' :
                                          h.quality_tag === 'V Low' ? 'bg-orange-100 text-orange-800' :
                                          h.quality_tag === 'VV Low' ? 'bg-red-100 text-red-800' :
                                          h.quality_tag === 'New' ? 'bg-purple-100 text-purple-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}>
                                          {h.quality_tag}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      ID: {h.id?.slice(0, 8)}... • Date: {h.purchase_date?.slice(0, 10)}
                                      {h.pledged && ' • Pledged'}
                                    </div>
                                  </div>
                                </label>
                                <button
                                  onClick={() => handleDeleteHolding(h.id, mergeHoldingsModal.symbol)}
                                  disabled={savingHealthItem === h.id}
                                  className="px-2 py-1 text-xs text-red-600 hover:bg-red-100 rounded border border-red-200 disabled:opacity-50"
                                  title="Delete this holding"
                                >
                                  {savingHealthItem === h.id ? '...' : '🗑️ Delete'}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-3 mb-4">
                          <p className="text-sm font-medium text-gray-700">After merge:</p>
                          <p className="text-lg font-bold text-green-700">
                            {mergeHoldingsModal.total_quantity?.toLocaleString('en-IN')} qty @ ₹
                            {mergeHoldingsModal.total_invested && mergeHoldingsModal.total_quantity
                              ? (mergeHoldingsModal.total_invested / mergeHoldingsModal.total_quantity).toFixed(2)
                              : '0'
                            } avg
                          </p>
                        </div>

                        <div className="flex justify-end gap-3">
                          <button
                            onClick={() => setMergeHoldingsModal(null)}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleMergeHoldings}
                            disabled={mergingHoldings || !selectedHoldingToKeep}
                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            {mergingHoldings ? 'Merging...' : `Merge ${mergeHoldingsModal.holdings?.length} Holdings`}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ============ SYMBOL HEALTH SECTION (Unified) ============ */}
                {symbolTab === 'health' && (
                  <div className="space-y-4">
                    {/* Filters Row */}
                    <div className="flex flex-wrap gap-3 items-center bg-gray-50 rounded-lg p-3">
                      {/* Search */}
                      <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search by company name..."
                            value={healthSearch}
                            onChange={(e) => setHealthSearch(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-blue-400"
                          />
                        </div>
                      </div>
                      
                      {/* Issue Type Filter */}
                      <div className="min-w-[200px]">
                        <select
                          value={issueFilter}
                          onChange={(e) => setIssueFilter(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-blue-400 bg-white"
                        >
                          <option value="all">All Issues ({symbolHealth.total_issues || 0})</option>
                          <optgroup label="Common Issues">
                            <option value="No ticker mapping">No ticker mapping</option>
                            <option value="Placeholder mapping (TESTMAP)">Placeholder (TESTMAP)</option>
                            <option value="Placeholder mapping (BULKTEST)">Placeholder (BULKTEST)</option>
                          </optgroup>
                          <optgroup label="All Issue Types">
                            {uniqueIssueTypes.map(issue => (
                              <option key={issue} value={issue}>{issue}</option>
                            ))}
                          </optgroup>
                        </select>
                      </div>
                      
                      {/* Clear Filters */}
                      {(healthFilter !== 'all' || issueFilter !== 'all' || healthSearch) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setHealthFilter('all');
                            setIssueFilter('all');
                            setHealthSearch('');
                          }}
                          className="text-xs"
                        >
                          Clear Filters
                        </Button>
                      )}
                      
                      {/* Results count */}
                      <span className="text-sm text-gray-500">
                        Showing {filteredHealthIssues.length} of {symbolHealth.total_issues || 0}
                      </span>
                    </div>

                    {/* Active Filters Display */}
                    {(healthFilter !== 'all' || issueFilter !== 'all') && (
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="text-gray-500">Active filters:</span>
                        {healthFilter !== 'all' && (
                          <span className={`px-2 py-1 rounded font-medium flex items-center gap-1 ${
                            healthFilter === 'no_price' ? 'bg-red-100 text-red-700' 
                              : healthFilter === 'invalid_ticker' ? 'bg-orange-100 text-orange-700'
                              : healthFilter === 'price_failed' ? 'bg-yellow-100 text-yellow-700'
                              : healthFilter === 'private' ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {healthFilter === 'no_price' ? '🔴 No Price' 
                              : healthFilter === 'invalid_ticker' ? '⚠️ Invalid Ticker'
                              : healthFilter === 'price_failed' ? '🟠 Stale Price'
                              : healthFilter === 'private' ? '🟢 Private'
                              : '🟡 Unlisted'}
                            <button onClick={() => setHealthFilter('all')} className="ml-1 hover:text-gray-900">×</button>
                          </span>
                        )}
                        {issueFilter !== 'all' && (
                          <span className="px-2 py-1 rounded font-medium bg-blue-100 text-blue-700 flex items-center gap-1">
                            {issueFilter.length > 30 ? issueFilter.substring(0, 30) + '...' : issueFilter}
                            <button onClick={() => setIssueFilter('all')} className="ml-1 hover:text-gray-900">×</button>
                          </span>
                        )}
                      </div>
                    )}

                    {/* Symbol Health Table */}
                    <div className="card">
                      {filteredHealthIssues.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <CheckCircle2 size={48} className="mx-auto mb-2 text-green-500" />
                          <p className="font-medium">
                            {healthFilter === 'all' ? 'All stocks are healthy!' : `No ${healthFilter === 'no_price' ? 'stocks without prices' : healthFilter === 'private' ? 'private stocks' : 'unlisted stocks'}`}
                          </p>
                          <p className="text-sm mt-1">No issues to fix.</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b bg-gray-50">
                                <th className="text-left py-3 px-3 font-semibold">Status</th>
                                <th className="text-left py-3 px-3 font-semibold">Company Name</th>
                                <th className="text-left py-3 px-3 font-semibold">Current Ticker</th>
                                <th className="text-right py-3 px-3 font-semibold">Qty</th>
                                <th className="text-right py-3 px-3 font-semibold">Cost</th>
                                <th className="text-left py-3 px-3 font-semibold">Issue / Info</th>
                                <th className="text-left py-3 px-3 font-semibold min-w-[150px]">Notes</th>
                                <th className="text-center py-3 px-3 font-semibold">Fix / Price</th>
                                <th className="text-center py-3 px-3 font-semibold">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredHealthIssues.map((issue, idx) => (
                                <React.Fragment key={issue.display_name}>
                                  <tr className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="py-2 px-3">
                                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                                        issue.status === 'no_price' 
                                          ? 'bg-red-100 text-red-700' 
                                          : issue.status === 'needs_review'
                                            ? 'bg-blue-100 text-blue-700'
                                            : issue.is_merger
                                              ? 'bg-purple-100 text-purple-700'
                                              : issue.status === 'invalid_ticker'
                                                ? 'bg-orange-100 text-orange-700'
                                                : 'bg-amber-100 text-amber-700'
                                      }`}>
                                        {issue.status_label}
                                      </span>
                                    </td>
                                    <td className="py-2 px-3 font-medium">{issue.display_name}</td>
                                    <td className="py-2 px-3">
                                      {issue.status === 'unlisted' ? (
                                        <span className="text-gray-400">—</span>
                                      ) : (
                                        <span className={`px-2 py-1 rounded text-xs ${
                                          issue.ticker_symbol === issue.display_name 
                                            ? 'bg-gray-100 text-gray-600' 
                                            : issue.ticker_symbol === 'TESTMAP' || issue.ticker_symbol === 'BULKTEST'
                                              ? 'bg-red-100 text-red-600'
                                              : issue.status === 'invalid_ticker'
                                                ? 'bg-orange-100 text-orange-600'
                                                : 'bg-blue-100 text-blue-600'
                                        }`}>
                                          {issue.ticker_symbol}
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-2 px-3 text-right">{issue.quantity?.toLocaleString()}</td>
                                    <td className="py-2 px-3 text-right">₹{issue.cost_basis?.toLocaleString()}</td>
                                    <td className="py-2 px-3 text-xs text-gray-600 max-w-xs">
                                      {issue.reason}
                                      {issue.new_company_name && (
                                        <div className="text-blue-600 mt-1">
                                          → Merged into: <strong>{issue.new_company_name}</strong>
                                          {issue.new_ticker && <span className="ml-1">({issue.new_ticker})</span>}
                                          {issue.swap_ratio && <span className="ml-1">[Swap: {issue.swap_ratio}]</span>}
                                        </div>
                                      )}
                                      {issue.manual_price && (
                                        <div className="text-purple-600 mt-1">
                                          Manual price: ₹{issue.manual_price.toLocaleString()}
                                        </div>
                                      )}
                                    </td>
                                    <td className="py-2 px-3">
                                      {/* Notes column - rich text editable for all records */}
                                      <div className="flex flex-col gap-1">
                                        <textarea
                                          placeholder="Add notes... (supports paste from web)"
                                          defaultValue={issue.notes || ''}
                                          onBlur={(e) => {
                                            if (e.target.value !== (issue.notes || '')) {
                                              saveNotes(issue.display_name, e.target.value);
                                            }
                                          }}
                                          rows={2}
                                          className="w-full min-w-[150px] px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:border-blue-400 bg-gray-50 focus:bg-white resize-y"
                                        />
                                      </div>
                                    </td>
                                    <td className="py-2 px-3">
                                      {/* Input column - show both ticker and price inputs for all statuses */}
                                      <div className="space-y-1">
                                        {/* Ticker input - always show */}
                                        <input
                                          type="text"
                                          placeholder="e.g. TCS"
                                          value={healthTickerInputs[issue.display_name] || ''}
                                          onChange={(e) => handleHealthTickerChange(issue.display_name, e.target.value)}
                                          className="w-24 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-400 uppercase"
                                        />
                                        {/* Price input - always show for setting manual price */}
                                        <div className="flex items-center gap-1">
                                          <div className="relative">
                                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">₹</span>
                                            <input
                                              type="number"
                                              placeholder={issue.manual_price ? issue.manual_price.toString() : "0"}
                                              value={healthPriceInputs[issue.display_name] || ''}
                                              onChange={(e) => handleHealthPriceChange(issue.display_name, e.target.value)}
                                              className="w-20 pl-5 pr-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-purple-400"
                                              min="0"
                                              step="0.01"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="py-2 px-3">
                                      {/* Actions column - dropdown with all options */}
                                      <div className="relative">
                                        <select
                                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:border-blue-400 cursor-pointer"
                                          value=""
                                          onChange={(e) => {
                                            const action = e.target.value;
                                            if (action === 'fix' && healthTickerInputs[issue.display_name]) {
                                              fixTickerMapping(issue.display_name);
                                            } else if (action === 'save_price' && healthPriceInputs[issue.display_name]) {
                                              saveManualPrice(issue.display_name);
                                            } else if (action === 'delist') {
                                              markAsUnlisted(issue.display_name);
                                            } else if (action === 'merged') {
                                              setExpandedMergerRow(expandedMergerRow === issue.display_name ? null : issue.display_name);
                                              setExpandedPrivateRow(null);
                                            } else if (action === 'private') {
                                              setExpandedPrivateRow(expandedPrivateRow === issue.display_name ? null : issue.display_name);
                                              setExpandedMergerRow(null);
                                            } else if (action === 'clear') {
                                              clearReview(issue.display_name);
                                            } else if (action === 'restore') {
                                              restoreToListed(issue.display_name);
                                            }
                                            e.target.value = '';
                                          }}
                                          disabled={savingHealthItem === issue.display_name}
                                        >
                                          <option value="">⚡ Actions</option>
                                          {healthTickerInputs[issue.display_name] && (
                                            <option value="fix">✓ Fix Ticker ({healthTickerInputs[issue.display_name]})</option>
                                          )}
                                          {healthPriceInputs[issue.display_name] && (
                                            <option value="save_price">💰 Save Price (₹{healthPriceInputs[issue.display_name]})</option>
                                          )}
                                          {issue.status !== 'unlisted' && issue.status !== 'private' && (
                                            <option value="delist">🟡 Mark Unlisted</option>
                                          )}
                                          <option value="merged">🔄 Mark as Merged...</option>
                                          <option value="private">🟢 Mark as Private...</option>
                                          {issue.status === 'needs_review' && (
                                            <option value="clear">✕ Clear from Review</option>
                                          )}
                                          {(issue.status === 'unlisted' || issue.status === 'private') && (
                                            <option value="restore">↩️ Restore to Listed</option>
                                          )}
                                        </select>
                                        {savingHealthItem === issue.display_name && (
                                          <RefreshCw size={12} className="absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-blue-500" />
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                  {/* Expanded Merger Form Row */}
                                  {expandedMergerRow === issue.display_name && (
                                    <tr className="bg-blue-50">
                                      <td colSpan="9" className="py-3 px-4">
                                        <div className="space-y-3">
                                          <p className="font-medium text-blue-800 text-sm">🔄 Mark as Merged - Enter merger details</p>
                                          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                                            <div>
                                              <label className="block text-xs text-gray-600 mb-1">New Company Name *</label>
                                              <input
                                                type="text"
                                                placeholder="e.g. HDFC Bank"
                                                value={healthMergerInputs[issue.display_name]?.newCompany || ''}
                                                onChange={(e) => handleMergerInputChange(issue.display_name, 'newCompany', e.target.value)}
                                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-400"
                                              />
                                            </div>
                                            <div>
                                              <label className="block text-xs text-gray-600 mb-1">New Ticker</label>
                                              <input
                                                type="text"
                                                placeholder="e.g. HDFCBANK"
                                                value={healthMergerInputs[issue.display_name]?.newTicker || ''}
                                                onChange={(e) => handleMergerInputChange(issue.display_name, 'newTicker', e.target.value.toUpperCase())}
                                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-400 uppercase"
                                              />
                                            </div>
                                            <div>
                                              <label className="block text-xs text-gray-600 mb-1">Swap Ratio</label>
                                              <input
                                                type="text"
                                                placeholder="e.g. 1:2 or 42:25"
                                                value={healthMergerInputs[issue.display_name]?.swapRatio || ''}
                                                onChange={(e) => handleMergerInputChange(issue.display_name, 'swapRatio', e.target.value)}
                                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-400"
                                              />
                                            </div>
                                            <div>
                                              <label className="block text-xs text-gray-600 mb-1">Notes</label>
                                              <input
                                                type="text"
                                                placeholder="Optional notes"
                                                value={healthMergerInputs[issue.display_name]?.notes || ''}
                                                onChange={(e) => handleMergerInputChange(issue.display_name, 'notes', e.target.value)}
                                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-400"
                                              />
                                            </div>
                                            <div className="flex items-end gap-2">
                                              <Button
                                                size="sm"
                                                onClick={() => markAsMerged(issue.display_name)}
                                                disabled={savingHealthItem === issue.display_name || !healthMergerInputs[issue.display_name]?.newCompany}
                                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                              >
                                                {savingHealthItem === issue.display_name ? (
                                                  <RefreshCw size={14} className="animate-spin" />
                                                ) : 'Save Merger'}
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setExpandedMergerRow(null)}
                                              >
                                                Cancel
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                  {/* Expanded Private Form Row */}
                                  {expandedPrivateRow === issue.display_name && (
                                    <tr className="bg-emerald-50">
                                      <td colSpan="9" className="py-3 px-4">
                                        <div className="space-y-3">
                                          <p className="font-medium text-emerald-800 text-sm">🟢 Mark as Private - Enter valuation details</p>
                                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                            <div>
                                              <label className="block text-xs text-gray-600 mb-1">Manual Share Price (₹)</label>
                                              <input
                                                type="number"
                                                placeholder="e.g. 150.00"
                                                value={healthPrivateInputs[issue.display_name]?.price || ''}
                                                onChange={(e) => handlePrivateInputChange(issue.display_name, 'price', e.target.value)}
                                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-emerald-400"
                                                min="0"
                                                step="0.01"
                                              />
                                            </div>
                                            <div className="md:col-span-2">
                                              <label className="block text-xs text-gray-600 mb-1">Notes (why private, source of valuation, etc.)</label>
                                              <input
                                                type="text"
                                                placeholder="e.g. Company went private in 2020, valuation from last known trade"
                                                value={healthPrivateInputs[issue.display_name]?.notes || ''}
                                                onChange={(e) => handlePrivateInputChange(issue.display_name, 'notes', e.target.value)}
                                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-emerald-400"
                                              />
                                            </div>
                                            <div className="flex items-end gap-2">
                                              <Button
                                                size="sm"
                                                onClick={() => markAsPrivate(issue.display_name)}
                                                disabled={savingHealthItem === issue.display_name}
                                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                              >
                                                {savingHealthItem === issue.display_name ? (
                                                  <RefreshCw size={14} className="animate-spin" />
                                                ) : 'Save as Private'}
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setExpandedPrivateRow(null)}
                                              >
                                                Cancel
                                              </Button>
                                            </div>
                                          </div>
                                          <p className="text-xs text-gray-500">
                                            💡 Private stocks with manual prices will be included in your portfolio total value.
                                          </p>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Mutual Funds Section */}
                {symbolTab === 'mutual-funds' && (
                  <div className="space-y-4">
                    <div className="flex gap-2 bg-gray-100 p-1 rounded-lg w-fit">
                      <button
                        onClick={() => setMfSubTab('unlinked')}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                          mfSubTab === 'unlinked' ? 'bg-white text-primary shadow-sm' : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        Unlinked ({unlinkedMFs.length})
                      </button>
                      <button
                        onClick={() => setMfSubTab('linked')}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                          mfSubTab === 'linked' ? 'bg-white text-primary shadow-sm' : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        Linked ({linkedMFs.length})
                      </button>
                    </div>

                    {/* Unlinked MFs */}
                    {mfSubTab === 'unlinked' && (
                      <div className="card">
                        {unlinkedMFs.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <Check size={48} className="mx-auto mb-2 text-green-500" />
                            <p>All mutual funds are linked!</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <p className="text-sm text-gray-600">
                              <AlertCircle size={16} className="inline mr-1" />
                              Link your mutual fund holdings to AMFI scheme codes for automatic NAV updates.
                            </p>
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead>
                                  <tr className="border-b">
                                    <th className="text-left py-3 px-4">Mutual Fund Name</th>
                                    <th className="text-right py-3 px-4">Units</th>
                                    <th className="text-right py-3 px-4">Value</th>
                                    <th className="text-center py-3 px-4">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {unlinkedMFs.map((mf) => (
                                    <tr key={mf.symbol} className="border-b hover:bg-gray-50">
                                      <td className="py-3 px-4">
                                        <p className="font-medium text-sm">{mf.symbol.replace('O-', '').replace('C-', '')}</p>
                                        <p className="text-xs text-gray-500">{mf.holding_ids?.length || 0} holding(s)</p>
                                      </td>
                                      <td className="text-right py-3 px-4">{mf.total_quantity?.toLocaleString()}</td>
                                      <td className="text-right py-3 px-4">{formatCurrency(mf.total_value)}</td>
                                      <td className="text-center py-3 px-4">
                                        <Button size="sm" onClick={() => openLinkDialog(mf)}>
                                          <Link2 size={14} className="mr-1" />
                                          Link
                                        </Button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Linked MFs */}
                    {mfSubTab === 'linked' && (
                      <div className="card">
                        {linkedMFs.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <AlertCircle size={48} className="mx-auto mb-2 text-amber-500" />
                            <p>No mutual funds linked yet.</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <p className="text-sm text-gray-600">
                              <CheckCircle2 size={16} className="inline mr-1 text-green-500" />
                              These mutual funds receive automatic NAV updates.
                            </p>
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead>
                                  <tr className="border-b bg-gray-50">
                                    <th className="text-left py-3 px-4">Mutual Fund</th>
                                    <th className="text-left py-3 px-4">Linked To</th>
                                    <th className="text-right py-3 px-4">NAV</th>
                                    <th className="text-right py-3 px-4">Units</th>
                                    <th className="text-right py-3 px-4">Value</th>
                                    <th className="text-center py-3 px-4">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {linkedMFs.map((mf) => (
                                    <tr key={mf.symbol} className="border-b hover:bg-gray-50">
                                      <td className="py-3 px-4">
                                        <p className="font-medium text-sm">{mf.symbol.replace('O-', '').replace('C-', '').substring(0, 35)}...</p>
                                        <p className="text-xs text-gray-500">{mf.holding_ids?.length || 0} holding(s)</p>
                                      </td>
                                      <td className="py-3 px-4">
                                        <p className="text-sm text-green-700">{mf.scheme_name || mf.display_name}</p>
                                        <p className="text-xs text-gray-500">Code: {mf.scheme_code}</p>
                                      </td>
                                      <td className="text-right py-3 px-4">
                                        <span className="font-medium text-green-600">₹{mf.current_price?.toFixed(2) || '0.00'}</span>
                                      </td>
                                      <td className="text-right py-3 px-4">{mf.total_quantity?.toLocaleString() || 0}</td>
                                      <td className="text-right py-3 px-4 font-medium">{formatCurrency(mf.total_value)}</td>
                                      <td className="text-center py-3 px-4">
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                          className="text-red-600 border-red-200 hover:bg-red-50"
                                          onClick={() => unlinkMutualFund(mf.symbol)}
                                          disabled={unlinking}
                                        >
                                          <Unlink size={14} className="mr-1" />
                                          Unlink
                                        </Button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ============ DATA INTEGRITY TAB ============ */}
        {mainTab === 'data' && (
          <div className="space-y-4">
            {/* Sub-tabs for Data section */}
            <div className="flex gap-2 border-b pb-2 mb-4">
              <button
                onClick={() => setDataTab('compare')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  dataTab === 'compare'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                📊 Compare Excel vs DB
              </button>
              <button
                onClick={() => setDataTab('summary')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  dataTab === 'summary'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                👥 Member Summary
              </button>
              <button
                onClick={() => setDataTab('integrity')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  dataTab === 'integrity'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                🗄️ View All Data
              </button>
            </div>

            {/* Member Summary Tab */}
            {dataTab === 'summary' && (
              <div className="space-y-4">
                <div className="card">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-lg font-semibold">Member Summary Report</h3>
                      <p className="text-sm text-gray-500">Compare these totals with your original Excel sheets to verify data integrity</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={fetchMemberSummary}
                      disabled={loadingSummary}
                    >
                      {loadingSummary ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />}
                      Refresh
                    </Button>
                  </div>

                  {loadingSummary ? (
                    <div className="text-center py-8">
                      <Loader2 className="animate-spin mx-auto mb-2" />
                      <p className="text-gray-500">Loading member summary...</p>
                    </div>
                  ) : memberSummary ? (
                    <div className="space-y-4">
                      {/* Totals Summary */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Total Holdings</p>
                          <p className="text-xl font-bold">{memberSummary.totals.total_holdings.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Cost Basis</p>
                          <p className="text-xl font-bold">₹{(memberSummary.totals.total_cost_basis / 10000000).toFixed(2)} Cr</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Current Value</p>
                          <p className="text-xl font-bold text-green-600">₹{(memberSummary.totals.total_current_value / 10000000).toFixed(2)} Cr</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Gain/Loss</p>
                          <p className={`text-xl font-bold ${memberSummary.totals.total_gain_loss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {memberSummary.totals.total_gain_loss >= 0 ? '+' : ''}₹{(memberSummary.totals.total_gain_loss / 10000000).toFixed(2)} Cr
                          </p>
                        </div>
                      </div>

                      {/* Unlisted Summary */}
                      {memberSummary.totals.total_unlisted > 0 && (
                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="text-xs text-amber-700">
                            <span className="font-semibold">Note:</span> {memberSummary.totals.total_unlisted} unlisted stock holdings 
                            (Cost: ₹{memberSummary.totals.total_unlisted_cost.toLocaleString('en-IN')}) are excluded from the totals above.
                          </p>
                        </div>
                      )}

                      {/* Member Table */}
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-gray-50">
                              <th className="text-left py-3 px-3 font-semibold">Member</th>
                              <th className="text-right py-3 px-3 font-semibold">Holdings</th>
                              <th className="text-right py-3 px-3 font-semibold">Stocks</th>
                              <th className="text-right py-3 px-3 font-semibold">MFs</th>
                              <th className="text-right py-3 px-3 font-semibold">Cost Basis</th>
                              <th className="text-right py-3 px-3 font-semibold">Current Value</th>
                              <th className="text-right py-3 px-3 font-semibold">Gain/Loss</th>
                              <th className="text-right py-3 px-3 font-semibold">%</th>
                            </tr>
                          </thead>
                          <tbody>
                            {memberSummary.members.map((m) => (
                              <tr key={m.member_id} className="border-b hover:bg-gray-50">
                                <td className="py-3 px-3 font-medium">{m.member_name}</td>
                                <td className="py-3 px-3 text-right">{m.holdings_count}</td>
                                <td className="py-3 px-3 text-right">{m.stocks_count}</td>
                                <td className="py-3 px-3 text-right">{m.mf_count}</td>
                                <td className="py-3 px-3 text-right">₹{(m.cost_basis / 10000000).toFixed(2)} Cr</td>
                                <td className="py-3 px-3 text-right font-medium">₹{(m.current_value / 10000000).toFixed(2)} Cr</td>
                                <td className={`py-3 px-3 text-right font-medium ${m.gain_loss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {m.gain_loss >= 0 ? '+' : ''}₹{(m.gain_loss / 10000000).toFixed(2)} Cr
                                </td>
                                <td className={`py-3 px-3 text-right ${m.gain_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {m.gain_percent >= 0 ? '+' : ''}{m.gain_percent.toFixed(1)}%
                                </td>
                              </tr>
                            ))}
                            {/* Total Row */}
                            <tr className="border-t-2 bg-gray-100 font-bold">
                              <td className="py-3 px-3">TOTAL</td>
                              <td className="py-3 px-3 text-right">{memberSummary.totals.total_holdings}</td>
                              <td className="py-3 px-3 text-right">{memberSummary.totals.total_stocks}</td>
                              <td className="py-3 px-3 text-right">{memberSummary.totals.total_mfs}</td>
                              <td className="py-3 px-3 text-right">₹{(memberSummary.totals.total_cost_basis / 10000000).toFixed(2)} Cr</td>
                              <td className="py-3 px-3 text-right">₹{(memberSummary.totals.total_current_value / 10000000).toFixed(2)} Cr</td>
                              <td className={`py-3 px-3 text-right ${memberSummary.totals.total_gain_loss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {memberSummary.totals.total_gain_loss >= 0 ? '+' : ''}₹{(memberSummary.totals.total_gain_loss / 10000000).toFixed(2)} Cr
                              </td>
                              <td className="py-3 px-3 text-right">-</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <p className="text-xs text-gray-400 text-right">
                        Generated: {new Date(memberSummary.generated_at).toLocaleString()}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>Click Refresh to load member summary</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Data Comparison Tab */}
            {dataTab === 'compare' && (
              <div className="space-y-4">
                <div className="card">
                  <h3 className="text-lg font-semibold mb-4">Compare Excel File with Database</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Upload an Excel file to compare its data against what&apos;s stored in the database. 
                    This will show quantity and price differences for each stock.
                  </p>
                  
                  <div className="flex flex-wrap gap-4 items-end">
                    <div className="flex-1 min-w-[250px]">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Select Excel File</label>
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleComparisonFileSelect}
                        className="input-field"
                      />
                      {comparisonFile && (
                        <p className="text-xs text-gray-500 mt-1">Selected: {comparisonFile.name}</p>
                      )}
                    </div>
                    <Button
                      onClick={runComparison}
                      disabled={!comparisonFile || comparing}
                      className="bg-primary text-white"
                    >
                      {comparing ? (
                        <>
                          <RefreshCw size={16} className="mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Search size={16} className="mr-2" />
                          Run Comparison
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Comparison Results */}
                {comparisonResult && (
                  <div className="space-y-4">
                    {/* Summary Card */}
                    <div className="card bg-gradient-to-r from-gray-50 to-white">
                      <h4 className="text-lg font-semibold mb-4">
                        Comparison Results: {comparisonResult.filename}
                        <span className="ml-2 px-2 py-1 bg-primary/10 text-primary rounded text-sm">
                          {comparisonResult.category}
                        </span>
                      </h4>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-3 bg-white rounded-lg border">
                          <p className="text-xs text-gray-500 uppercase">Excel Holdings</p>
                          <p className="text-2xl font-bold">{comparisonResult.summary.excel_total_holdings}</p>
                        </div>
                        <div className="p-3 bg-white rounded-lg border">
                          <p className="text-xs text-gray-500 uppercase">DB Holdings</p>
                          <p className="text-2xl font-bold">{comparisonResult.summary.db_total_holdings}</p>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-xs text-green-600 uppercase">Matches</p>
                          <p className="text-2xl font-bold text-green-700">{comparisonResult.summary.matches}</p>
                        </div>
                        <div className={`p-3 rounded-lg border ${comparisonResult.summary.discrepancies > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                          <p className={`text-xs uppercase ${comparisonResult.summary.discrepancies > 0 ? 'text-red-600' : 'text-green-600'}`}>Discrepancies</p>
                          <p className={`text-2xl font-bold ${comparisonResult.summary.discrepancies > 0 ? 'text-red-700' : 'text-green-700'}`}>
                            {comparisonResult.summary.discrepancies}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t">
                        <div>
                          <p className="text-xs text-gray-500">Excel Total Value</p>
                          <p className="text-lg font-semibold">₹{comparisonResult.summary.excel_total_value?.toLocaleString('en-IN')}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">DB Total Value</p>
                          <p className="text-lg font-semibold">₹{comparisonResult.summary.db_total_value?.toLocaleString('en-IN')}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Value Difference</p>
                          <p className={`text-lg font-semibold ${comparisonResult.summary.value_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {comparisonResult.summary.value_difference >= 0 ? '+' : ''}₹{comparisonResult.summary.value_difference?.toLocaleString('en-IN')}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Discrepancies Table */}
                    {comparisonResult.discrepancies.length > 0 && (
                      <div className="card">
                        <h4 className="text-lg font-semibold mb-4">
                          Discrepancies ({comparisonResult.discrepancies.length})
                          {comparisonResult.has_more && <span className="text-sm font-normal text-gray-500 ml-2">(showing top 100)</span>}
                        </h4>
                        
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b bg-gray-50">
                                <th className="text-left py-2 px-3">Member</th>
                                <th className="text-left py-2 px-3">Symbol</th>
                                <th className="text-left py-2 px-3">Issue</th>
                                <th className="text-right py-2 px-3">Excel Qty</th>
                                <th className="text-right py-2 px-3">DB Qty</th>
                                <th className="text-right py-2 px-3">Qty Diff</th>
                                <th className="text-right py-2 px-3">Excel Price</th>
                                <th className="text-right py-2 px-3">DB Price</th>
                                <th className="text-right py-2 px-3">Value Diff</th>
                                <th className="text-center py-2 px-3">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {comparisonResult.discrepancies.map((d, idx) => (
                                <tr key={idx} className="border-b hover:bg-gray-50">
                                  <td className="py-2 px-3 font-medium">{d.member}</td>
                                  <td className="py-2 px-3">{d.symbol}</td>
                                  <td className="py-2 px-3">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getIssueTypeColor(d.issue_type)}`}>
                                      {d.issue_type?.replace(/_/g, ' ')}
                                    </span>
                                  </td>
                                  <td className="py-2 px-3 text-right">{d.excel_qty?.toLocaleString()}</td>
                                  <td className="py-2 px-3 text-right">{d.db_qty?.toLocaleString()}</td>
                                  <td className={`py-2 px-3 text-right font-medium ${d.qty_diff !== 0 ? (d.qty_diff > 0 ? 'text-green-600' : 'text-red-600') : ''}`}>
                                    {d.qty_diff !== 0 ? (d.qty_diff > 0 ? '+' : '') + d.qty_diff?.toLocaleString() : '-'}
                                  </td>
                                  <td className="py-2 px-3 text-right">₹{d.excel_price?.toLocaleString()}</td>
                                  <td className="py-2 px-3 text-right">₹{d.db_price?.toLocaleString()}</td>
                                  <td className={`py-2 px-3 text-right font-medium ${d.value_diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {d.value_diff >= 0 ? '+' : ''}₹{d.value_diff?.toLocaleString()}
                                  </td>
                                  <td className="py-2 px-3 text-center">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        moveToSymbolHealth(d.symbol, `Data discrepancy: ${d.issue_type?.replace(/_/g, ' ')}`);
                                        setSymbolTab('health');
                                        setMainTab('symbols');
                                      }}
                                      className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                                    >
                                      🔍 Investigate
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {comparisonResult.discrepancies.length === 0 && (
                      <div className="card bg-green-50 border border-green-200 text-center py-8">
                        <CheckCircle className="mx-auto mb-2 text-green-600" size={48} />
                        <h4 className="text-lg font-semibold text-green-800">Perfect Match!</h4>
                        <p className="text-green-600">All data in the Excel file matches the database records.</p>
                      </div>
                    )}

                    {/* Matches Table */}
                    {comparisonResult.matches && comparisonResult.matches.length > 0 && (
                      <div className="card mt-4">
                        <h3 className="font-semibold mb-3 text-green-700">
                          ✓ Matches ({comparisonResult.summary.matches})
                          {comparisonResult.has_more_matches && <span className="text-sm font-normal text-gray-500 ml-2">(showing top 100)</span>}
                        </h3>
                        <div className="overflow-x-auto max-h-64 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-green-50">
                              <tr className="text-left text-green-700">
                                <th className="p-2">Member</th>
                                <th className="p-2">Symbol</th>
                                <th className="p-2 text-right">Quantity</th>
                                <th className="p-2 text-right">Value</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {comparisonResult.matches.map((m, idx) => (
                                <tr key={idx} className="hover:bg-green-50">
                                  <td className="p-2 font-medium">{m.member}</td>
                                  <td className="p-2 text-xs max-w-xs truncate" title={m.symbol}>{m.symbol}</td>
                                  <td className="p-2 text-right">{m.excel_qty?.toLocaleString('en-IN')}</td>
                                  <td className="p-2 text-right">₹{m.excel_value?.toLocaleString('en-IN')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Data Integrity Tab (existing functionality) */}
            {dataTab === 'integrity' && (
              <>
                {dataLoading ? (
              <div className="card text-center py-8">
                <RefreshCw size={32} className="mx-auto mb-2 animate-spin text-primary" />
                <p>Loading data...</p>
              </div>
            ) : !selectedDataset ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {datasets.map(dataset => (
                  <div 
                    key={dataset.id}
                    onClick={() => fetchRecords(dataset.id)}
                    className="card cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-primary"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">{dataset.name}</h3>
                      <ChevronRight size={20} className="text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500 mb-3">{dataset.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-primary">{dataset.count}</span>
                      <span className="text-xs text-gray-400">records</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Back button and header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { setSelectedDataset(null); setRecords([]); setDataSearchTerm(''); }}
                    >
                      ← Back
                    </Button>
                    <div>
                      <h2 className="text-xl font-bold">{selectedDataset.name}</h2>
                      <p className="text-sm text-gray-500">{selectedDataset.count} records</p>
                    </div>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="text"
                      placeholder="Search records..."
                      value={dataSearchTerm}
                      onChange={(e) => setDataSearchTerm(e.target.value)}
                      className="input-field pl-9 w-64"
                    />
                  </div>
                </div>

                {/* Records table */}
                <div className="card">
                  {loadingRecords ? (
                    <div className="text-center py-8">
                      <RefreshCw size={24} className="mx-auto mb-2 animate-spin" />
                      <p>Loading records...</p>
                    </div>
                  ) : filteredRecords.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Database size={48} className="mx-auto mb-2 opacity-50" />
                      <p>No records found</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            {getDisplayColumns(selectedDataset.id).map(col => (
                              <th key={col} className="text-left py-2 px-3 font-medium">
                                {col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </th>
                            ))}
                            <th className="text-center py-2 px-3 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRecords.slice(0, 100).map((record, idx) => (
                            <tr key={record.id || idx} className="border-b hover:bg-gray-50">
                              {getDisplayColumns(selectedDataset.id).map(col => (
                                <td key={col} className="py-2 px-3">
                                  {editingRecord?.id === record.id ? (
                                    <input
                                      type="text"
                                      value={editingRecord[col] ?? ''}
                                      onChange={(e) => setEditingRecord({...editingRecord, [col]: e.target.value})}
                                      className="input-field text-sm w-full"
                                    />
                                  ) : (
                                    <span className="truncate block max-w-[200px]" title={String(record[col])}>
                                      {formatValue(record[col])}
                                    </span>
                                  )}
                                </td>
                              ))}
                              <td className="py-2 px-3 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  {editingRecord?.id === record.id ? (
                                    <>
                                      <Button size="sm" variant="ghost" onClick={handleSaveRecord}>
                                        <Check size={14} className="text-green-600" />
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={() => setEditingRecord(null)}>
                                        <X size={14} className="text-gray-500" />
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <Button size="sm" variant="ghost" onClick={() => setEditingRecord({...record})}>
                                        <Edit2 size={14} />
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        onClick={() => setShowDeleteConfirm(record.id)}
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        <Trash2 size={14} />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {filteredRecords.length > 100 && (
                        <p className="text-center text-sm text-gray-500 py-2">
                          Showing 100 of {filteredRecords.length} records
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
              </>
            )}
          </div>
        )}

        {/* ============ DIALOGS ============ */}
        
        {/* MF Linking Dialog */}
        <Dialog open={linkingDialogOpen} onOpenChange={setLinkingDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Link Mutual Fund</DialogTitle>
              <DialogDescription>
                Search and link your mutual fund to the official AMFI scheme for automatic NAV updates.
              </DialogDescription>
            </DialogHeader>
            
            {selectedMF && (
              <div className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Linking:</p>
                  <p className="font-medium">{selectedMF.symbol.replace('O-', '').replace('C-', '')}</p>
                  <p className="text-sm text-gray-600">
                    {selectedMF.total_quantity?.toLocaleString()} units · {formatCurrency(selectedMF.total_value)}
                  </p>
                </div>

                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      placeholder="Search by fund name or scheme code..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && searchMutualFunds()}
                      className="input-field w-full"
                      style={{ paddingLeft: '2.5rem' }}
                    />
                  </div>
                  <Button onClick={searchMutualFunds} disabled={searching}>
                    {searching ? <RefreshCw size={16} className="animate-spin" /> : 'Search'}
                  </Button>
                </div>
                
                <p className="text-xs text-gray-500">
                  💡 Tip: Enter the AMFI scheme code directly (e.g., 103197) for exact match
                </p>

                {searchResults.length > 0 && (
                  <div className="max-h-64 overflow-y-auto border rounded-lg">
                    {searchResults.map((result) => (
                      <div 
                        key={result.schemeCode}
                        className="p-3 border-b hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                        onClick={() => linkMutualFund(result.schemeCode, result.schemeName)}
                      >
                        <div className="flex-1">
                          <p className="font-medium text-sm">{result.schemeName}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-gray-500">Code: {result.schemeCode}</span>
                            {result.nav ? (
                              <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded">
                                NAV: ₹{result.nav.toFixed(2)}
                              </span>
                            ) : result.navError ? (
                              <span className="text-xs text-red-500">{result.navError}</span>
                            ) : null}
                          </div>
                        </div>
                        <Button size="sm" variant="outline" disabled={linking}>
                          {linking ? <RefreshCw size={14} className="animate-spin" /> : 'Select'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {searchResults.length === 0 && searchQuery && !searching && (
                  <p className="text-center text-gray-500 py-4">
                    No results found. Try a different search term.
                  </p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Upload Summary Dialog */}
        <Dialog open={summaryOpen} onOpenChange={setSummaryOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="text-green-500" size={24} />
                Upload Successful
              </DialogTitle>
            </DialogHeader>
            {uploadSummary && (
              <div className="space-y-3">
                {uploadSummary.type === 'cheatsheet' ? (
                  <>
                    <p>Cheatsheet data imported successfully!</p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="p-3 bg-gray-50 rounded">
                        <p className="text-gray-500">Stocks Updated</p>
                        <p className="font-bold text-lg">{uploadSummary.stocks_updated}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded">
                        <p className="text-gray-500">Categories</p>
                        <p className="font-bold text-lg">{uploadSummary.categories_count}</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="p-3 bg-gray-50 rounded">
                        <p className="text-gray-500">Holdings Added</p>
                        <p className="font-bold text-lg">{uploadSummary.totalHoldings}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded">
                        <p className="text-gray-500">Transactions</p>
                        <p className="font-bold text-lg">{uploadSummary.totalTransactions}</p>
                      </div>
                    </div>
                    {uploadSummary.totalSkippedDuplicates > 0 && (
                      <p className="text-sm text-amber-600">
                        <Info size={14} className="inline mr-1" />
                        {uploadSummary.totalSkippedDuplicates} duplicate entries skipped
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => setSummaryOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reverse Upload Confirmation Dialog */}
        <Dialog open={confirmReverseOpen} onOpenChange={setConfirmReverseOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">Reverse Upload</DialogTitle>
              <DialogDescription>
                This will delete all holdings and transactions from this upload. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {selectedUpload && (
              <div className="p-3 bg-red-50 rounded-lg text-sm">
                <p className="font-medium">{selectedUpload.filename}</p>
                <p className="text-gray-600">
                  {selectedUpload.holdings_count} holdings · {selectedUpload.transactions_count} transactions
                </p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmReverseOpen(false)}>Cancel</Button>
              <Button 
                variant="destructive" 
                onClick={handleReverseUpload}
                disabled={reversing}
              >
                {reversing ? 'Reversing...' : 'Yes, Reverse Upload'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Record Confirmation Dialog */}
        <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete Record</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this record? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>Cancel</Button>
              <Button 
                variant="destructive" 
                onClick={() => handleDeleteRecord(showDeleteConfirm)}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Imports;
